import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { areEqual } from 'Utils/equalityChecks';
import { setToaster } from 'Containers/Core/actions';
import { Modal } from 'Components/Modal';
import { PurpleButton, LoadMoreButton } from 'Components/Button';
import { SpinnerBlock } from 'Components/SpinnerBlock';

import { listCompanyEquipmentAssets, deployAssetToRoom } from '../actions';
import { EquipmentAsset } from '../types';

import classes from './addUnitToRoomModal.module.css';

interface Props {
  isOpen: boolean;
  companyId: number | string;
  roomId: number;
  onClose: (e: any) => void;
  // Called after each successful deploy so the parent can refresh the room's deployed list. The
  // modal stays open (with the unit removed) so several units can be added to the same room.
  onSuccess: () => void;
}

// Pick an available serialized unit and deploy it into the current room. Queries the paginated,
// searchable pool filtered to status=available (default 25/page) so it scales, rather than pulling
// the whole pool. Deploy mints one idempotency key per unit and reuses it across retries.
function AddUnitToRoomModalContainer({ isOpen, companyId, roomId, onClose, onSuccess }: Props) {
  const dispatch = useDispatch();

  const [assets, setAssets] = useState<EquipmentAsset[]>([]);
  const [fetching, setFetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [deployBusyId, setDeployBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // Optional back-dated deploy date (date_in). Empty = server "now". Applies to every unit deployed
  // in this session.
  const [deployDate, setDeployDate] = useState('');
  // Optional end date (date_out). When set, records a COMPLETED past stay (unit stays available)
  // instead of an active deploy. Empty = active deploy.
  const [deployEndDate, setDeployEndDate] = useState('');

  // One idempotency key per unit, reused until its deploy succeeds so a retry after a lost response
  // replays the same key (backend dedupes) instead of opening a second placement.
  const deployKeys = useRef<Record<number, string>>({});
  // Guards against overlapping list requests (double-clicking Load More / Search).
  const loadingRef = useRef(false);

  const load = useCallback(
    async (nextPage = 1, searchTerm = '') => {
      if (!companyId || loadingRef.current) return;
      loadingRef.current = true;
      if (nextPage > 1) {
        setLoadingMore(true);
      } else {
        setFetching(true);
        setFailed(false);
      }
      const response: any = await dispatch(
        listCompanyEquipmentAssets(companyId as string, {
          status: 'available',
          search: searchTerm || undefined,
          page: nextPage,
        })
      );
      if (!response) {
        if (nextPage === 1) setFailed(true);
        else dispatch(setToaster('Couldn’t load more units. Please try again.', false));
        setFetching(false);
        setLoadingMore(false);
        loadingRef.current = false;
        return;
      }
      const data: EquipmentAsset[] = response?.data ?? [];
      setAssets((prev) => (nextPage > 1 ? [...prev, ...data] : data));
      setPage(response?.meta?.current_page ?? nextPage);
      setLastPage(response?.meta?.last_page ?? 1);
      setTotal(response?.meta?.total ?? data.length);
      setFetching(false);
      setLoadingMore(false);
      loadingRef.current = false;
    },
    [companyId, dispatch]
  );

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setError(null);
      setDeployDate('');
      setDeployEndDate('');
      load(1, '');
    } else {
      setAssets([]);
    }
  }, [isOpen, load]);

  const onSearchSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      load(1, search);
    },
    [load, search]
  );

  const onLoadMore = useCallback(() => load(page + 1, search), [load, page, search]);

  const onDeploy = useCallback(
    async (asset: EquipmentAsset) => {
      if (deployBusyId) return;
      const idempotencyKey = deployKeys.current[asset.id] ?? uuidv4();
      deployKeys.current[asset.id] = idempotencyKey;
      setDeployBusyId(asset.id);
      setError(null);
      const response: any = await dispatch(
        deployAssetToRoom(asset.id, {
          room_id: roomId,
          date_in: deployDate || undefined,
          date_out: deployEndDate || undefined,
          idempotency_key: idempotencyKey,
        })
      );
      setDeployBusyId(null);
      if (response?.data) {
        delete deployKeys.current[asset.id];
        // Drop it from the available list (a completed past stay leaves it available, but it's
        // already listed above; an active deploy removes it) and refresh the room behind us.
        setAssets((prev) => prev.filter((item) => item.id !== asset.id));
        onSuccess();
        return;
      }
      setError(response?.error?.message || 'Couldn’t deploy this unit. Please try again.');
    },
    [deployBusyId, roomId, deployDate, deployEndDate, dispatch, onSuccess]
  );

  return (
    <Modal
      id="add-unit-to-room-modal"
      title="Add Equipment to Room"
      isOpen={isOpen}
      modalHeader
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={onClose}
    >
      <div className="d-flex flex-column w-100" data-testid="add-unit-to-room">
        <form className="d-flex gap-2 mb-3" onSubmit={onSearchSubmit}>
          <input
            type="text"
            className="form-control"
            placeholder="Search name, serial, or asset tag…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="available-search"
          />
          <PurpleButton type="submit" className={classes.searchButton}>
            Search
          </PurpleButton>
        </form>

        <div className="d-flex gap-2 mb-3">
          <div className="flex-fill">
            <label className="form-label" htmlFor="deploy-date">
              Deploy date (optional)
            </label>
            <input
              id="deploy-date"
              type="date"
              className="form-control"
              value={deployDate}
              onChange={(e) => setDeployDate(e.target.value)}
              data-testid="deploy-date"
            />
          </div>
          <div className="flex-fill">
            <label className="form-label" htmlFor="deploy-end-date">
              End date (optional)
            </label>
            <input
              id="deploy-end-date"
              type="date"
              className="form-control"
              value={deployEndDate}
              onChange={(e) => setDeployEndDate(e.target.value)}
              data-testid="deploy-end-date"
            />
          </div>
        </div>
        <small className="text-muted d-block mb-3">
          Deploy date blank = now. Set an end date to log a completed past stay (unit stays available).
        </small>

        {error && (
          <p className="text-danger mb-2" role="alert" data-testid="add-unit-error">
            {error}
          </p>
        )}

        {failed && (
          <div className="d-flex flex-column align-items-center p-3" role="alert" data-testid="available-error">
            <p className="text-muted mb-2">Couldn’t load available units.</p>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => load(1, search)}>
              Retry
            </button>
          </div>
        )}
        {!failed && fetching && <SpinnerBlock fetching />}
        {!failed && !fetching && (
          <ul className={classes.list} data-testid="available-units">
            {assets.length === 0 && (
              <li className={classes.empty} data-testid="available-empty">
                {search ? 'No available units match your search.' : 'No available units.'}
              </li>
            )}
            {assets.map((asset) => {
              const makeModel = [asset.manufacturer, asset.model].filter(Boolean).join(' ');
              const ids = [asset.serial_number, asset.asset_tag].filter(Boolean).join(' · ');
              const busy = deployBusyId === asset.id;
              return (
                <li key={asset.id} className={classes.row} data-testid="available-row">
                  <div className={classes.name}>
                    <span className={classes.assetName}>{asset.name}</span>
                    {makeModel && <span className={classes.meta}>{makeModel}</span>}
                    {ids && <span className={classes.meta}>{ids}</span>}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={!!deployBusyId}
                    onClick={() => onDeploy(asset)}
                  >
                    {busy ? 'Deploying…' : 'Deploy here'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {loadingMore && <SpinnerBlock fetching />}
        {!fetching && !loadingMore && !failed && page < lastPage && (
          <LoadMoreButton type="Units" totalItems={total - assets.length} loadMoreItemsClick={onLoadMore} />
        )}

        <div className="modal-footer d-flex justify-content-end mt-3">
          <PurpleButton type="button" onClick={onClose}>
            Done
          </PurpleButton>
        </div>
      </div>
    </Modal>
  );
}

const AddUnitToRoomModalMemo = memo(AddUnitToRoomModalContainer, areEqual);

export { AddUnitToRoomModalMemo as AddUnitToRoomModal };
